import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useSession } from '@/lib/SessionContext';
import { toast } from 'sonner';
import {
  ShieldCheck, Bell, Search, Check, Save,
  Eye, EyeOff, UserCheck, Edit3, DollarSign, Lock,
  LayoutDashboard, Wallet, BarChart2, Megaphone, Building2,
  AlertTriangle, CheckCircle2, ChevronRight, ChevronDown, Folder, FolderOpen, ShieldAlert, Sparkles,
  Zap, Smartphone, LayoutList, FileText, UploadCloud, ShoppingBag,
  Truck, Receipt, Star, ArrowUpDown, Filter
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getSidebarNav } from '@/components/layout/AppLayout';
import { NOTIFICATION_EVENTS, dispatchNotification } from '@/lib/notificationEngine';

const PORTAL_BADGE_COLORS = {
  WORKSPACE: 'bg-slate-100 text-slate-800 border-slate-300',
  FINANCE: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  MARKETING: 'bg-purple-50 text-purple-800 border-purple-200',
  PURCHASING: 'bg-amber-50 text-amber-800 border-amber-200',
  PRODUKSI: 'bg-blue-50 text-blue-800 border-blue-200',
  ANALYTICS: 'bg-indigo-50 text-indigo-800 border-indigo-200',
};

const PAGES_WITH_PRICE = [
  'stok_inventori', 'marketing_inventory', 'purchasing_inventory', 'kalkulator_hpp', 'purchasing_items', 'riwayat_hpp',
  'laporan', 'biaya_operasional', 'database_brand', 'dashboard_finance',
  'rekening_mutasi', 'finance_calculator', 'finance_sph_list', 'invoice_po', 'finance_custom_invoices',
  'finance_payables', 'finance_receivables', 'finance_payables_receivables',
  'purchasing_payables', 'purchasing_requests', 'finance_purchasing_requests',
  'analytics_margin_keseluruhan', 'analytics_laporan_keuangan', 'analytics_margin_po', 'analytics_margin_sample',
  'analytics_margin_custom', 'analytics_ranking', 'analytics_omset', 'biaya_produksi', 'gaji_karyawan', 'finance_laporan',
  'marketing_calculator', 'marketing_sph_list', 'marketing_invoices', 'marketing_custom_invoices', 'marketing_receivables',
  'barang_harus_dibeli', 'purchasing_items_to_buy', 'purchasing_items_po'
];

const PAGES_WITH_PIC = [
  'po_produksi', 'invoice_po', 'finance_custom_invoices', 'marketing_calculator', 'marketing_sph_list',
  'marketing_invoices', 'marketing_custom_invoices', 'sample_racikan', 'data_klien', 'purchasing_client_data',
  'database_brand', 'delivery', 'purchasing_delivery', 'tugas_waktu_produksi', 'tugas_saya', 'return',
  'produksi_reject', 'finance_reject_tracking', 'barang_harus_dibeli', 'purchasing_items_to_buy',
  'purchasing_items_po', 'purchasing_requests', 'finance_purchasing_requests'
];

export default function WorkspacePageAccess() {
  const qc = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { member: currentMember, refreshMember } = useSession();

  // Active Sub-category Tab: 'access' | 'notification'
  const isNotifRoute = location.pathname.includes('notification');
  const [activeTab, setActiveTab] = useState(isNotifRoute ? 'notification' : 'access');

  useEffect(() => {
    if (location.pathname.includes('notification-settings')) {
      setActiveTab('notification');
    } else if (location.pathname.includes('access')) {
      setActiveTab('access');
    }
  }, [location.pathname]);

  const [selectedUserId, setSelectedUserId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Access state
  const [activePortalIndex, setActivePortalIndex] = useState(0);
  const [userAccess, setUserAccess] = useState({});

  // Notification state
  const [userNotificationPrefs, setUserNotificationPrefs] = useState({});
  const [masterPush, setMasterPush] = useState(true);
  const [masterInApp, setMasterInApp] = useState(true);
  const [masterCritical, setMasterCritical] = useState(true);
  const [isTestingNotification, setIsTestingNotification] = useState(false);
  const [notifCategoryFilter, setNotifCategoryFilter] = useState('ALL');
  const [notifSortBy, setNotifSortBy] = useState('default'); // 'default' | 'category_asc' | 'category_desc'
  const [openModules, setOpenModules] = useState({}); // default all collapsed

  // 1. Fetch Active Team Members
  const { data: teamMembers = [], isLoading } = useQuery({
    queryKey: ['team-members-unified-access-notif'],
    queryFn: () => base44.entities.TeamMember.list('full_name'),
  });

  const activeMembers = useMemo(() => {
    return teamMembers.filter(m => !m.deleted_at && m.status !== 'nonaktif');
  }, [teamMembers]);

  // Set default selected user
  useEffect(() => {
    if (activeMembers.length > 0 && !selectedUserId) {
      const defaultUser = activeMembers.find(m => m.id === currentMember?.id) || activeMembers[0];
      setSelectedUserId(defaultUser.id);
    }
  }, [activeMembers, selectedUserId, currentMember]);

  const selectedUser = useMemo(() => {
    return activeMembers.find(m => m.id === selectedUserId) || null;
  }, [activeMembers, selectedUserId]);

  // Detect which portals this user has active access to
  const userActivePortals = useMemo(() => {
    const list = [];
    if (userAccess['portal_finance'] && userAccess['portal_finance'] !== 'none') list.push('FINANCE');
    if (userAccess['portal_marketing'] && userAccess['portal_marketing'] !== 'none') list.push('MARKETING');
    if (userAccess['portal_production'] && userAccess['portal_production'] !== 'none') list.push('PRODUKSI');
    if (userAccess['portal_purchasing'] && userAccess['portal_purchasing'] !== 'none') list.push('PURCHASING');
    if (userAccess['portal_analytics'] && userAccess['portal_analytics'] !== 'none') list.push('ANALYTICS');
    if (userAccess['portal_workspace'] && userAccess['portal_workspace'] !== 'none') list.push('WORKSPACE');

    // Default fallback based on role if no explicit portal access set yet
    if (list.length === 0 && selectedUser) {
      const r = selectedUser?.role?.toLowerCase() || '';
      if (r === 'finance') list.push('FINANCE');
      if (r === 'marketing') list.push('MARKETING');
      if (r === 'purchasing') list.push('PURCHASING');
      if (['owner', 'developer', 'admin'].includes(r)) list.push('WORKSPACE', 'FINANCE', 'MARKETING', 'PURCHASING', 'PRODUKSI', 'ANALYTICS');
    }
    return list;
  }, [userAccess, selectedUser]);

  // Order portal filter buttons: [Semua Portal], [Portals that user has access to], [Other portals]
  // Except when user has access to all portals -> Keep default natural order!
  const sortedPortalFilters = useMemo(() => {
    const allPortals = [
      { id: 'WORKSPACE', label: 'Workspace' },
      { id: 'FINANCE', label: 'Finance' },
      { id: 'MARKETING', label: 'Marketing' },
      { id: 'PURCHASING', label: 'Purchasing' },
      { id: 'PRODUKSI', label: 'Produksi' },
      { id: 'ANALYTICS', label: 'Analytics' },
    ];

    // If user has full access (All portals), keep standard natural order
    if (userActivePortals.length >= allPortals.length || userActivePortals.length === 0) {
      return [
        { id: 'ALL', label: 'Semua Portal' },
        ...allPortals
      ];
    }

    const accessible = allPortals.filter(p => userActivePortals.includes(p.id));
    const others = allPortals.filter(p => !userActivePortals.includes(p.id));

    return [
      { id: 'ALL', label: 'Semua Portal' },
      ...accessible,
      ...others
    ];
  }, [userActivePortals]);

  const filteredAndSortedNotifEvents = useMemo(() => {
    let list = Object.values(NOTIFICATION_EVENTS);
    if (notifCategoryFilter !== 'ALL') {
      list = list.filter(e => {
        const portals = e.relatedPortals || [e.category];
        return portals.includes(notifCategoryFilter) || e.category === notifCategoryFilter;
      });
    }

    list = [...list].sort((a, b) => {
      const aPortals = a.relatedPortals || [a.category];
      const bPortals = b.relatedPortals || [b.category];

      const aMatchesUser = aPortals.some(p => userActivePortals.includes(p));
      const bMatchesUser = bPortals.some(p => userActivePortals.includes(p));

      // Events matching user's active portals always sorted to TOP!
      if (aMatchesUser && !bMatchesUser) return -1;
      if (!aMatchesUser && bMatchesUser) return 1;

      if (notifSortBy === 'category_asc') {
        return a.category.localeCompare(b.category);
      } else if (notifSortBy === 'category_desc') {
        return b.category.localeCompare(a.category);
      }
      return 0;
    });

    return list;
  }, [notifCategoryFilter, notifSortBy, userActivePortals]);

  // Group events by Module (e.g. 'Daftar SPH', 'Invoice & PO')
  const groupedNotifEventsByModule = useMemo(() => {
    const groups = {};
    filteredAndSortedNotifEvents.forEach(event => {
      const mod = event.moduleName || 'Lainnya';
      if (!groups[mod]) groups[mod] = [];
      groups[mod].push(event);
    });
    return groups;
  }, [filteredAndSortedNotifEvents]);

  // Sync access and notification prefs when selected user changes
  useEffect(() => {
    if (selectedUser) {
      // Sync Access
      try {
        const rawAccess = typeof selectedUser.page_access === 'string'
          ? JSON.parse(selectedUser.page_access || '{}')
          : (selectedUser.page_access || {});
        setUserAccess(rawAccess);
      } catch {
        setUserAccess({});
      }

      // Sync Notification Preferences
      try {
        const rawNotif = typeof selectedUser.notification_preferences === 'string'
          ? JSON.parse(selectedUser.notification_preferences || '{}')
          : (selectedUser.notification_preferences || {});
        setUserNotificationPrefs(rawNotif);
      } catch {
        setUserNotificationPrefs({});
      }
    }
  }, [selectedUser]);

  // Portals configuration for Access Tab
  const portals = useMemo(() => [
    { title: "Portal Workspace", icon: LayoutDashboard, accessKey: "portal_workspace", nav: getSidebarNav(null, '/office/workspace') },
    { title: "Portal Finance", icon: DollarSign, accessKey: "portal_finance", nav: getSidebarNav(null, '/office/finance') },
    { title: "Portal Purchasing", icon: Wallet, accessKey: "portal_purchasing", nav: getSidebarNav(null, '/office/purchasing') },
    { title: "Portal Analytics", icon: BarChart2, accessKey: "portal_analytics", nav: getSidebarNav(null, '/office/analytics') },
    { title: "Portal Produksi", icon: Building2, accessKey: "portal_production", nav: getSidebarNav(null, '/office/production') },
    { title: "Portal Marketing", icon: Megaphone, accessKey: "portal_marketing", nav: getSidebarNav(null, '/office/marketing') },
  ], []);

  const activePortal = portals[activePortalIndex] || portals[0];

  const currentPortalNavItems = useMemo(() => {
    const rawItems = activePortal.nav?.flatMap(g => g.items) || [];
    const flattened = [];
    const seenKeys = new Set();
    rawItems.forEach(item => {
      if (item.accessKey && !seenKeys.has(item.accessKey)) {
        seenKeys.add(item.accessKey);
        flattened.push(item);
      }
      if (item.children) {
        item.children.forEach(child => {
          if (child.accessKey && !seenKeys.has(child.accessKey)) {
            seenKeys.add(child.accessKey);
            flattened.push(child);
          }
        });
      }
    });
    return flattened;
  }, [activePortal]);

  // ----------------------------------------------------
  // MUTATIONS (SAVE ACCESS & SAVE NOTIFICATIONS)
  // ----------------------------------------------------
  const saveAccessMutation = useMutation({
    mutationFn: async ({ userId, access }) => {
      return base44.entities.TeamMember.update(userId, {
        page_access: JSON.stringify(access)
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['team-members-unified-access-notif'] });
      if (selectedUserId === currentMember?.id && refreshMember) {
        await refreshMember();
      }
      toast.success('Hak akses pengguna berhasil disimpan!');
    },
    onError: (err) => {
      toast.error('Gagal menyimpan hak akses: ' + err.message);
    }
  });

  const saveNotifMutation = useMutation({
    mutationFn: async ({ userId, preferences }) => {
      return base44.entities.TeamMember.update(userId, {
        notification_preferences: JSON.stringify(preferences)
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-members-unified-access-notif'] });
      toast.success('Pengaturan notifikasi berhasil disimpan!');
    },
    onError: (err) => {
      toast.error('Gagal menyimpan pengaturan notifikasi: ' + err.message);
    }
  });

  const handleSaveCurrent = () => {
    if (!selectedUserId) return;
    if (activeTab === 'access') {
      saveAccessMutation.mutate({ userId: selectedUserId, access: userAccess });
    } else {
      saveNotifMutation.mutate({ userId: selectedUserId, preferences: userNotificationPrefs });
    }
  };

  // Helper Access Handlers
  const setPageAccessLevel = (key, val) => setUserAccess(prev => ({ ...prev, [key]: val }));
  const togglePortalAccess = (portalKey) => {
    setUserAccess(prev => {
      const current = prev[portalKey] || 'none';
      const next = current === 'none' ? 'edit' : 'none';
      return { ...prev, [portalKey]: next };
    });
  };
  const setPortalAll = (val) => {
    setUserAccess(prev => {
      const next = { ...prev };
      next[activePortal.accessKey] = val === 'none' ? 'none' : 'edit';
      currentPortalNavItems.forEach(item => {
        if (item.accessKey) next[item.accessKey] = val;
      });
      return next;
    });
  };
  const setGlobalAll = (val) => {
    setUserAccess(prev => {
      const next = { ...prev };
      portals.forEach(p => {
        next[p.accessKey] = val === 'none' ? 'none' : 'edit';
        const items = p.nav?.flatMap(g => g.items) || [];
        items.forEach(it => {
          if (it.accessKey) next[it.accessKey] = val;
          if (it.children) it.children.forEach(c => { if (c.accessKey) next[c.accessKey] = val; });
        });
      });
      return next;
    });
    toast.info(`Semua portal diatur ke: ${val === 'edit' ? 'Bisa Edit' : val === 'view' ? 'Hanya Lihat' : 'Terkunci'}`);
  };
  const togglePrice = (key) => {
    setUserAccess(prev => {
      const nextVal = !(prev[`${key}_show_price`] || prev[`${key}_price`]);
      return {
        ...prev,
        [`${key}_show_price`]: nextVal,
        [`${key}_price`]: nextVal
      };
    });
  };

  const togglePic = (key) => {
    setUserAccess(prev => {
      if (key === 'sample_racikan') {
        const current = prev[`${key}_is_pic`] || prev[`${key}_allow_pic`];
        let next;
        if (!current) next = 'brand';
        else if (current === 'brand') next = 'pembuat';
        else if (current === 'pembuat') next = 'both';
        else next = false;
        return {
          ...prev,
          [`${key}_is_pic`]: next,
          [`${key}_allow_pic`]: next,
          [`${key}_pic`]: next
        };
      }
      if (key === 'tugas_saya') {
        const current = prev[`${key}_is_pic`] || prev[`${key}_allow_pic`];
        let next;
        if (!current || current === false) next = 'mulai';
        else if (current === 'mulai' || current === true) next = 'memantau';
        else if (current === 'memantau') next = 'validasi';
        else if (current === 'validasi') next = 'all';
        else next = false;
        return {
          ...prev,
          [`${key}_is_pic`]: next,
          [`${key}_allow_pic`]: next,
          [`${key}_pic`]: next
        };
      }
      const val = !(prev[`${key}_allow_pic`] || prev[`${key}_is_pic`]);
      return {
        ...prev,
        [`${key}_allow_pic`]: val,
        [`${key}_is_pic`]: val,
        [`${key}_pic`]: val
      };
    });
  };

  // Helper Notification Handlers
  const handleNotifModeChange = (eventId, mode) => {
    setUserNotificationPrefs(prev => ({
      ...prev,
      [eventId]: {
        ...(prev[eventId] || {}),
        mode: mode,
        push: mode !== 'off',
        in_app: mode !== 'off',
      }
    }));
  };
  const handleNotifChannelToggle = (eventId, channel, checked) => {
    setUserNotificationPrefs(prev => ({
      ...prev,
      [eventId]: {
        ...(prev[eventId] || { mode: 'all' }),
        [channel]: checked,
      }
    }));
  };
  const handleResetNotifToDefault = () => {
    const defaultObj = {};
    Object.values(NOTIFICATION_EVENTS).forEach(ev => {
      const isRoleDefault = ev.defaultRoles.includes(selectedUser?.role?.toLowerCase());
      defaultObj[ev.id] = {
        mode: isRoleDefault || ['owner', 'developer'].includes(selectedUser?.role) ? 'all' : (ev.allowPicMode ? 'pic_only' : 'off'),
        push: true,
        in_app: true,
      };
    });
    setUserNotificationPrefs(defaultObj);
    toast.info('Preferensi notifikasi di-reset ke rekomendasi default role.');
  };

  const handleTestNotification = async () => {
    if (!selectedUser) {
      toast.error('Pilih pengguna tujuan terlebih dahulu.');
      return;
    }
    setIsTestingNotification(true);
    try {
      const testInvoiceNo = 'INV-TEST-' + Math.floor(1000 + Math.random() * 9000);
      const actorUser = currentMember || { id: 'dev_tester', full_name: 'Developer Testing', role: 'developer' };

      const res = await dispatchNotification({
        eventId: 'invoice_created',
        title: 'Invoice Baru Dibuat (Uji Coba)',
        body: `Uji coba notifikasi invoice ${testInvoiceNo} untuk Brand Satala Luxury berhasil dikirim.`,
        badgeText: 'FINANCE',
        actor: { id: actorUser.id, full_name: actorUser.full_name, role: actorUser.role },
        targetPic: { id: selectedUser.id, full_name: selectedUser.full_name, role: selectedUser.role },
        meta: {
          invoice_id: testInvoiceNo,
          brand_name: 'Satala Luxury Test',
          total_amount: 15000000,
        },
        actionUrl: `/office/finance/invoices`,
      });

      qc.invalidateQueries({ queryKey: ['db-notifications-bell'] });
      toast.success(`⚡ Berhasil menguji kirim notifikasi ke ${res.count} pengguna! Cek ikon lonceng di atas.`);
    } catch (e) {
      console.error(e);
      toast.error('Gagal mengirim notifikasi tes: ' + e.message);
    } finally {
      setIsTestingNotification(false);
    }
  };

  const filteredMembers = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return activeMembers;
    return activeMembers.filter(m => 
      m.full_name?.toLowerCase().includes(q) || 
      m.role?.toLowerCase().includes(q) ||
      m.department?.toLowerCase().includes(q)
    );
  }, [activeMembers, searchQuery]);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
      
      {/* 1. DYNAMIC HEADER (Berubah menyesuaikan sub kategori aktif) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-foreground tracking-tight flex items-center gap-2">
            {activeTab === 'access' ? (
              <>
                <ShieldCheck className="w-6 h-6 text-[#C9A227]" />
                Manajemen Hak Akses Pengguna
              </>
            ) : (
              <>
                <Bell className="w-6 h-6 text-[#C9A227]" />
                Pengaturan Notifikasi
              </>
            )}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {activeTab === 'access'
              ? 'Atur izin modul, kemampuan edit, nominal harga, dan wewenang PIC untuk setiap pengguna.'
              : 'Kelola preferensi dan penerimaan notifikasi cerdas berdasarkan pengguna dan role divisi.'}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {activeTab === 'notification' && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleTestNotification}
              disabled={isTestingNotification}
              className="border-amber-400/60 text-[#8C6208] hover:bg-amber-50 gap-1.5 text-xs font-semibold shadow-xs"
            >
              <Zap className="w-3.5 h-3.5 text-amber-600 fill-amber-600" />
              {isTestingNotification ? 'Mengirim Tes...' : 'Test Kirim Notifikasi'}
            </Button>
          )}

          <Button
            size="sm"
            onClick={handleSaveCurrent}
            disabled={saveAccessMutation.isPending || saveNotifMutation.isPending}
            className="bg-[#C9A227] hover:bg-[#B38F1E] text-white gap-1.5 text-xs font-semibold shadow-sm"
          >
            <Save className="w-3.5 h-3.5" />
            {saveAccessMutation.isPending || saveNotifMutation.isPending
              ? 'Menyimpan...'
              : activeTab === 'access' ? 'Simpan Hak Akses' : 'Simpan Pengaturan'}
          </Button>
        </div>
      </div>

      {/* 2. DYNAMIC TOP BANNER / PRESET CARDS (Berubah menyesuaikan sub kategori) */}
      {activeTab === 'access' ? (
        /* Top Banner Hak Akses: Preset Akses Global Cepat */
        <div className="p-3.5 rounded-2xl bg-card border border-border/80 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber-50 text-[#C9A227] flex items-center justify-center shrink-0 border border-amber-200">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <p className="font-bold text-xs text-foreground">Preset Akses Global Cepat</p>
              <p className="text-[11px] text-muted-foreground">Terapkan wewenang ke seluruh 6 portal sekaligus untuk pengguna terpilih.</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setGlobalAll('edit')}
              className="text-xs h-7 border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-bold"
            >
              <Edit3 className="w-3.5 h-3.5 mr-1" /> Semua Portal Bisa Edit
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setGlobalAll('view')}
              className="text-xs h-7 border-blue-300 text-blue-700 hover:bg-blue-50 font-bold"
            >
              <Eye className="w-3.5 h-3.5 mr-1" /> Semua Portal Hanya Lihat
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setGlobalAll('none')}
              className="text-xs h-7 border-slate-300 text-slate-700 hover:bg-slate-100 font-bold"
            >
              <Lock className="w-3.5 h-3.5 mr-1" /> Kunci Semua Portal
            </Button>
          </div>
        </div>
      ) : (
        /* Top Banner Notifikasi: 3 Master Toggles */
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card className="border border-border/80 shadow-xs bg-card/60">
            <CardContent className="p-3.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-[#C9A227] flex items-center justify-center shrink-0">
                  <Smartphone className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-bold text-xs text-foreground">Push Notification</p>
                  <p className="text-[11px] text-muted-foreground">Kirim langsung ke layar HP/perangkat</p>
                </div>
              </div>
              <Switch checked={masterPush} onCheckedChange={setMasterPush} />
            </CardContent>
          </Card>

          <Card className="border border-border/80 shadow-xs bg-card/60">
            <CardContent className="p-3.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-700 flex items-center justify-center shrink-0">
                  <LayoutList className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-bold text-xs text-foreground">Notifikasi In-App</p>
                  <p className="text-[11px] text-muted-foreground">Tersimpan di menu lonceng web</p>
                </div>
              </div>
              <Switch checked={masterInApp} onCheckedChange={setMasterInApp} />
            </CardContent>
          </Card>

          <Card className="border border-border/80 shadow-xs bg-card/60">
            <CardContent className="p-3.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-700 flex items-center justify-center shrink-0">
                  <ShieldAlert className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-bold text-xs text-foreground">Notifikasi Kritis / Penting</p>
                  <p className="text-[11px] text-muted-foreground">Event darurat selalu diteruskan</p>
                </div>
              </div>
              <Switch checked={masterCritical} onCheckedChange={setMasterCritical} />
            </CardContent>
          </Card>
        </div>
      )}

      {/* 3. SUB-CATEGORY TABS SWITCHER (Hak Akses vs Notifikasi) */}
      <div className="flex gap-2 border-b border-border/60 pb-2">
        <button
          onClick={() => setActiveTab('access')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
            activeTab === 'access'
              ? 'bg-[#C9A227] text-white shadow-xs'
              : 'text-muted-foreground hover:bg-muted/50'
          }`}
        >
          <ShieldCheck className="w-4 h-4" />
          Hak Akses
        </button>
        <button
          onClick={() => setActiveTab('notification')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
            activeTab === 'notification'
              ? 'bg-[#C9A227] text-white shadow-xs'
              : 'text-muted-foreground hover:bg-muted/50'
          }`}
        >
          <Bell className="w-4 h-4" />
          Notifikasi
        </button>
      </div>

      {/* 4. MAIN LAYOUT GRID (Left: User List | Right: Access or Notification Config) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        
        {/* Left Side: Consistent User Selector */}
        <div className="lg:col-span-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
              Daftar Pengguna
            </h2>
            <Badge variant="outline" className="text-[10px]">
              {activeMembers.length} Karyawan
            </Badge>
          </div>

          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Cari nama atau jabatan..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-xs bg-card"
            />
          </div>

          <div className="space-y-1.5 max-h-[580px] overflow-y-auto pr-1">
            {filteredMembers.map(m => {
              const isSelected = m.id === selectedUserId;
              return (
                <div
                  key={m.id}
                  onClick={() => setSelectedUserId(m.id)}
                  className={`p-3 rounded-xl border transition-all cursor-pointer flex items-center justify-between gap-3 ${
                    isSelected
                      ? 'bg-amber-50/70 border-amber-300 text-amber-950 shadow-xs'
                      : 'bg-card border-border/70 hover:bg-muted/40 text-foreground'
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-xs font-bold shrink-0 text-foreground">
                      {m.full_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-xs truncate">{m.full_name}</p>
                      <p className="text-[10px] text-muted-foreground capitalize truncate">{m.role} · {m.department || 'Staff'}</p>
                    </div>
                  </div>
                  {isSelected && <Check className="w-4 h-4 text-amber-600 shrink-0" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Side: Matrix Content (Access or Notification) */}
        <div className="lg:col-span-8 space-y-4">
          
          {/* TAB 1: HAK AKSES MATRIX */}
          {activeTab === 'access' && (
            <div className="p-4 rounded-2xl bg-card border border-border/80 shadow-xs space-y-4">
              {/* User Target Banner */}
              <div className="flex items-center justify-between pb-3 border-b border-border/50 flex-wrap gap-2">
                <div>
                  <p className="text-xs text-muted-foreground">Mengatur Hak Akses Untuk:</p>
                  <p className="font-bold text-sm text-foreground">
                    {selectedUser ? `${selectedUser.full_name} (${selectedUser.role?.toUpperCase()})` : 'Pilih Pengguna'}
                  </p>
                </div>
                <Badge className="bg-[#C9A227] text-white">
                  {selectedUser?.position || selectedUser?.role || 'Staff'}
                </Badge>
              </div>

              {/* Portal Selection Tabs Grid (6 Portals) */}
              <div>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
                  Pilih Portal Yang Ingin Diatur:
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {portals.map((p, idx) => {
                    const Icon = p.icon;
                    const isSelected = idx === activePortalIndex;
                    const portalStatus = userAccess[p.accessKey] || 'none';
                    const isLocked = portalStatus === 'none';

                    return (
                      <div
                        key={p.accessKey}
                        onClick={() => setActivePortalIndex(idx)}
                        className={`p-2.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between gap-2 ${
                          isSelected
                            ? 'border-[#C9A227] bg-[#F9F5EC] shadow-2xs'
                            : 'border-border/70 hover:bg-muted/40 bg-card'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                            isSelected ? 'bg-[#C9A227] text-white' : 'bg-muted text-muted-foreground'
                          }`}>
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-xs truncate">{p.title.replace('Portal ', '')}</p>
                            <span className={`text-[9px] font-bold ${
                              isLocked ? 'text-slate-400' : 'text-emerald-700'
                            }`}>
                              {isLocked ? 'Terkunci' : 'Aktif'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Current Active Portal Detail Box */}
              <div className="p-3.5 rounded-xl border border-amber-200/80 bg-amber-50/20 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <activePortal.icon className="w-5 h-5 text-[#C9A227]" />
                    <div>
                      <h3 className="font-bold text-xs text-foreground">{activePortal.title}</h3>
                      <p className="text-[10px] text-muted-foreground">Pengaturan akses halaman & tombol Set PIC untuk {activePortal.title}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPortalAll('edit')}
                      className="text-[10px] h-6 px-2 border-emerald-300 text-emerald-700 font-bold"
                    >
                      Semua Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPortalAll('view')}
                      className="text-[10px] h-6 px-2 border-blue-300 text-blue-700 font-bold"
                    >
                      Semua Lihat
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPortalAll('none')}
                      className="text-[10px] h-6 px-2 border-slate-300 text-slate-700 font-bold"
                    >
                      Kunci Portal
                    </Button>
                  </div>
                </div>

                {/* Master Access to Enter this Portal */}
                <div className="p-2.5 rounded-xl bg-card border border-border/80 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-muted-foreground" />
                    <span className="font-bold text-xs text-foreground">Akses Masuk {activePortal.title}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant={userAccess[activePortal.accessKey] !== 'none' ? 'default' : 'outline'}
                      onClick={() => togglePortalAccess(activePortal.accessKey)}
                      className={`text-xs h-7 gap-1 font-bold ${
                        userAccess[activePortal.accessKey] !== 'none'
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          : 'text-slate-500'
                      }`}
                    >
                      {userAccess[activePortal.accessKey] !== 'none' ? (
                        <><CheckCircle2 className="w-3.5 h-3.5" /> Portal Terbuka</>
                      ) : (
                        <><Lock className="w-3.5 h-3.5" /> Portal Terkunci</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* List of Pages in this Portal */}
              <div className="space-y-2">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                  Daftar Halaman & Hak Akses ({currentPortalNavItems.length} Halaman)
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {currentPortalNavItems.map((item) => {
                    const Icon = item.icon || LayoutDashboard;
                    const itemKey = item.accessKey;
                    const currentMode = userAccess[itemKey] || 'none';
                    const hasPriceConfig = PAGES_WITH_PRICE.includes(itemKey);
                    const hasPicConfig = PAGES_WITH_PIC.includes(itemKey);

                    const showPrice = !!(userAccess[`${itemKey}_show_price`] || userAccess[`${itemKey}_price`]);
                    const picVal = userAccess[`${itemKey}_allow_pic`] || userAccess[`${itemKey}_is_pic`] || userAccess[`${itemKey}_pic`];

                    const isPicActive = !!picVal && picVal !== 'none';

                    return (
                      <div key={itemKey} className="p-3 rounded-xl bg-card border border-border/80 shadow-xs space-y-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                            <span className="font-bold text-xs text-foreground truncate">{item.label}</span>
                          </div>
                        </div>

                        {/* Controls Row: Access Level Dropdown + Set PIC Button + Harga Button */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {/* Access Level Selector */}
                          <div className="flex-1 min-w-[110px]">
                            <Select
                              value={currentMode}
                              onValueChange={(val) => setPageAccessLevel(itemKey, val)}
                            >
                              <SelectTrigger className={`h-7 text-[11px] font-bold ${
                                currentMode === 'edit'
                                  ? 'border-emerald-300 text-emerald-700 bg-emerald-50/60'
                                  : currentMode === 'view'
                                  ? 'border-blue-300 text-blue-700 bg-blue-50/60'
                                  : 'border-slate-200 text-slate-400 bg-slate-50/60'
                              }`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="edit" className="text-xs text-emerald-700 font-bold">
                                  Bisa Edit
                                </SelectItem>
                                <SelectItem value="view" className="text-xs text-blue-700 font-bold">
                                  Hanya Lihat
                                </SelectItem>
                                <SelectItem value="none" className="text-xs text-slate-500">
                                  Tidak Ada Akses
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Set PIC Clickable Button */}
                          {hasPicConfig && (
                            <button
                              type="button"
                              onClick={() => togglePic(itemKey)}
                              disabled={currentMode === 'none'}
                              className={`flex items-center justify-center gap-1 px-2.5 h-7 text-[11px] rounded-md border font-bold transition-all shrink-0 ${
                                currentMode === 'none'
                                  ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed opacity-40'
                                  : itemKey === 'sample_racikan' && picVal === 'pembuat'
                                    ? 'border-amber-400 bg-amber-100 text-amber-900 shadow-2xs font-extrabold ring-1 ring-amber-400/50'
                                    : itemKey === 'sample_racikan' && picVal === 'brand'
                                      ? 'border-blue-400 bg-blue-100 text-blue-900 shadow-2xs font-extrabold ring-1 ring-blue-400/50'
                                      : itemKey === 'sample_racikan' && (picVal === 'both' || picVal === true)
                                        ? 'border-emerald-400 bg-emerald-100 text-emerald-900 shadow-2xs font-extrabold ring-1 ring-emerald-400/50'
                                        : itemKey === 'tugas_saya' && (picVal === 'all' || picVal === 'both')
                                          ? 'border-indigo-400 bg-gradient-to-r from-emerald-50 to-indigo-50 text-indigo-950 shadow-2xs font-extrabold ring-1 ring-indigo-400/50'
                                          : itemKey === 'tugas_saya' && picVal === 'memantau'
                                            ? 'border-blue-400 bg-blue-100 text-blue-900 shadow-2xs font-extrabold ring-1 ring-blue-400/50'
                                            : itemKey === 'tugas_saya' && picVal === 'validasi'
                                              ? 'border-purple-400 bg-purple-100 text-purple-900 shadow-2xs font-extrabold ring-1 ring-purple-400/50'
                                              : itemKey === 'tugas_saya' && (picVal === 'mulai' || picVal === true)
                                                ? 'border-emerald-400 bg-emerald-100 text-emerald-900 shadow-2xs font-extrabold ring-1 ring-emerald-400/50'
                                                : isPicActive
                                                  ? 'border-amber-400 bg-amber-100 text-amber-900 shadow-2xs font-extrabold ring-1 ring-amber-400/50'
                                                  : 'border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100'
                              }`}
                              title={`Klik untuk mengatur wewenang PIC untuk ${item.label}`}
                            >
                              <UserCheck className="w-3.5 h-3.5" />
                              <span>
                                {itemKey === 'sample_racikan' && picVal === 'brand' ? 'PIC Brand' :
                                 itemKey === 'sample_racikan' && picVal === 'pembuat' ? 'PIC Pembuat' :
                                 itemKey === 'sample_racikan' && (picVal === 'both' || picVal === true) ? 'PIC Keduanya' :
                                 itemKey === 'tugas_saya' && (picVal === 'all' || picVal === 'both') ? 'PIC & Validasi' :
                                 itemKey === 'tugas_saya' && (picVal === 'mulai' || picVal === true) ? 'PIC Mulai' :
                                 itemKey === 'tugas_saya' && picVal === 'memantau' ? 'Memantau' :
                                 itemKey === 'tugas_saya' && picVal === 'validasi' ? 'Validasi QC' :
                                 'Set PIC'}
                              </span>
                            </button>
                          )}

                          {/* Harga Clickable Button */}
                          {hasPriceConfig && (
                            <button
                              type="button"
                              onClick={() => togglePrice(itemKey)}
                              disabled={currentMode === 'none'}
                              className={`flex items-center justify-center gap-1 px-2.5 h-7 text-[11px] rounded-md border transition-all shrink-0 ${
                                currentMode === 'none'
                                  ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed opacity-40'
                                  : showPrice
                                    ? 'border-amber-300 bg-amber-50 text-amber-800 font-bold shadow-2xs'
                                    : 'border-slate-200 bg-slate-50 text-slate-400 hover:bg-slate-100'
                              }`}
                              title="Tampilkan / Sembunyikan Nominal Harga"
                            >
                              {showPrice ? <Eye className="w-3 h-3 text-amber-600" /> : <EyeOff className="w-3 h-3 text-slate-400" />}
                              <span>Harga</span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: NOTIFIKASI MATRIX (Matching User Mockup Layout with Portal Filters) */}
          {activeTab === 'notification' && (
            <div className="p-4 rounded-2xl bg-card border border-border/80 shadow-xs space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-border/50 flex-wrap gap-2">
                <div>
                  <h3 className="font-bold text-sm text-foreground">
                    Pengaturan Notifikasi untuk: <span className="text-[#C9A227]">{selectedUser?.full_name || 'Pilih Pengguna'}</span> <span className="text-xs text-muted-foreground font-normal">({selectedUser?.role?.toUpperCase() || ''})</span>
                  </h3>
                  {/* User's Active Portals Badges */}
                  <div className="flex items-center gap-1.5 flex-wrap mt-1">
                    <span className="text-[10px] text-muted-foreground font-medium">Akses Portal Pengguna:</span>
                    {userActivePortals.length > 0 ? (
                      userActivePortals.map(p => (
                        <span key={p} className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-muted border border-border text-foreground">
                          {p}
                        </span>
                      ))
                    ) : (
                      <span className="text-[10px] text-slate-400 italic">Belum ada akses portal</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-[10px]">
                    {filteredAndSortedNotifEvents.length} Event Notifikasi
                  </Badge>
                </div>
              </div>

              {/* Portal / Category Filter Buttons Bar (Sorted by User Access) */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] font-bold text-muted-foreground uppercase mr-1 flex items-center gap-1">
                  <Filter className="w-3 h-3 text-[#C9A227]" /> Filter Portal:
                </span>
                {sortedPortalFilters.map(cat => {
                  const isSelected = notifCategoryFilter === cat.id;
                  const isUserAccessible = cat.id !== 'ALL' && userActivePortals.includes(cat.id);

                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setNotifCategoryFilter(cat.id)}
                      className={`px-3 py-1 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                        isSelected
                          ? 'bg-[#C9A227] text-white shadow-2xs ring-1 ring-amber-400'
                          : isUserAccessible
                          ? 'bg-amber-50/80 text-amber-900 border border-amber-200/80 hover:bg-amber-100/70 font-bold'
                          : 'bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <span>{cat.label}</span>
                      {isUserAccessible && !isSelected && (
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" title="Portal ini diakses oleh pengguna" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Table Column Headers with Sort on KATEGORI & Expand/Collapse All */}
              <div className="hidden sm:grid sm:grid-cols-12 gap-3 px-3 py-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-wider border-b border-border/40 items-center">
                <div className="col-span-6 flex items-center justify-between pr-4">
                  <span>MODUL & NOTIFIKASI EVENT</span>
                  <button
                    type="button"
                    onClick={() => {
                      const modKeys = Object.keys(groupedNotifEventsByModule);
                      const allOpen = modKeys.length > 0 && modKeys.every(m => openModules[m]);
                      if (allOpen) {
                        setOpenModules({});
                      } else {
                        const next = {};
                        modKeys.forEach(m => next[m] = true);
                        setOpenModules(next);
                      }
                    }}
                    className="text-[10px] font-bold text-[#C9A227] hover:underline normal-case"
                  >
                    {Object.keys(groupedNotifEventsByModule).length > 0 && Object.keys(groupedNotifEventsByModule).every(m => openModules[m])
                      ? 'Tutup Semua Folder'
                      : 'Buka Semua Folder'}
                  </button>
                </div>
                <div 
                  className="col-span-2 text-center flex items-center justify-center gap-1 cursor-pointer hover:text-foreground select-none"
                  onClick={() => {
                    setNotifSortBy(prev => prev === 'category_asc' ? 'category_desc' : prev === 'category_desc' ? 'default' : 'category_asc');
                  }}
                  title="Klik untuk mengurutkan berdasarkan Portal/Kategori"
                >
                  <span>PORTAL TERKAIT</span>
                  <ArrowUpDown className="w-3 h-3 text-[#C9A227]" />
                </div>
                <div className="col-span-1 text-center">PENTING</div>
                <div className="col-span-1 text-center">PUSH</div>
                <div className="col-span-2 text-right">CAKUPAN</div>
              </div>

              {/* Notification Matrix Module Folders / Accordions (Default: Collapsed) */}
              <div className="space-y-3">
                {Object.keys(groupedNotifEventsByModule).length === 0 ? (
                  <div className="py-8 text-center text-xs text-muted-foreground">
                    Belum ada event notifikasi untuk portal ini yang difinalisasi.
                  </div>
                ) : (
                  Object.entries(groupedNotifEventsByModule).map(([modName, events]) => {
                    const isOpen = !!openModules[modName];
                    const modulePortals = Array.from(new Set(events.flatMap(e => e.relatedPortals || [e.category])));

                    return (
                      <div key={modName} className="rounded-2xl border border-border/80 overflow-hidden bg-card/60 shadow-2xs transition-all">
                        {/* Module Folder Header Button */}
                        <button
                          type="button"
                          onClick={() => setOpenModules(prev => ({ ...prev, [modName]: !isOpen }))}
                          className="w-full px-3.5 py-3 bg-muted/20 hover:bg-muted/40 flex items-center justify-between transition-colors text-left"
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-7 h-7 rounded-xl bg-amber-500/10 text-[#C9A227] flex items-center justify-center shrink-0 border border-amber-200/50">
                              {isOpen ? <FolderOpen className="w-4 h-4" /> : <Folder className="w-4 h-4" />}
                            </div>
                            <span className="font-bold text-xs sm:text-sm text-foreground truncate">
                              Modul: {modName}
                            </span>
                            <Badge variant="outline" className="text-[10px] h-5 bg-background font-bold px-2">
                              {events.length} Event
                            </Badge>
                          </div>

                          <div className="flex items-center gap-2">
                            <div className="hidden sm:flex items-center gap-1">
                              {modulePortals.map(portal => (
                                <span
                                  key={portal}
                                  className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase border tracking-wider ${
                                    PORTAL_BADGE_COLORS[portal] || 'bg-muted text-muted-foreground border-border'
                                  }`}
                                >
                                  {portal}
                                </span>
                              ))}
                            </div>
                            <div className="w-6 h-6 rounded-lg bg-background border border-border/60 flex items-center justify-center text-muted-foreground">
                              {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            </div>
                          </div>
                        </button>

                        {/* Expanded Module Events Rows */}
                        {isOpen && (
                          <div className="divide-y divide-border/40 px-2 sm:px-3 py-1 bg-background/50 border-t border-border/50">
                            {events.map((event) => {
                              const currentPref = userNotificationPrefs[event.id] || {};
                              
                              // Default to 'off' (Nonaktif) unless explicitly configured!
                              const activeMode = currentPref.mode || 'off';
                              const isPush = currentPref.push === true && activeMode !== 'off';
                              const isImportant = !!currentPref.important;

                              // Resolve Icon
                              const Icon = event.icon === 'FileText' ? FileText :
                                           event.icon === 'UploadCloud' ? UploadCloud :
                                           event.icon === 'ShoppingBag' ? ShoppingBag :
                                           event.icon === 'CheckCircle2' ? CheckCircle2 :
                                           event.icon === 'Truck' ? Truck :
                                           event.icon === 'AlertTriangle' ? AlertTriangle :
                                           event.icon === 'Receipt' ? Receipt : Bell;

                              const portalsList = event.relatedPortals || [event.category];

                              return (
                                <div key={event.id} className="py-2.5 px-2 flex flex-col sm:grid sm:grid-cols-12 gap-3 sm:items-center hover:bg-muted/30 rounded-xl transition-colors">
                                  {/* Left: Event Icon + Title & Description */}
                                  <div className="sm:col-span-6 flex items-center gap-3 min-w-0">
                                    <div className="w-9 h-9 rounded-xl bg-[#FFFDF9] border border-[#E6D5B8]/60 flex items-center justify-center text-[#8C6208] shrink-0 shadow-2xs">
                                      <Icon className="w-4 h-4 stroke-[1.75]" />
                                    </div>
                                    <div className="min-w-0">
                                      <p className="font-bold text-xs text-foreground truncate">{event.name}</p>
                                      <p className="text-[11px] text-muted-foreground truncate mt-0.5">{event.description}</p>
                                    </div>
                                  </div>

                                  {/* Related Portals Badges */}
                                  <div className="sm:col-span-2 flex sm:justify-center items-center gap-1 flex-wrap">
                                    {portalsList.map(portal => (
                                      <span
                                        key={portal}
                                        className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase border tracking-wider ${
                                          PORTAL_BADGE_COLORS[portal] || 'bg-muted text-muted-foreground border-border'
                                        }`}
                                      >
                                        {portal}
                                      </span>
                                    ))}
                                  </div>

                                  {/* Penting (Star Toggle) */}
                                  <div className="sm:col-span-1 flex sm:justify-center items-center">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setUserNotificationPrefs(prev => ({
                                          ...prev,
                                          [event.id]: {
                                            ...(prev[event.id] || {}),
                                            important: !isImportant
                                          }
                                        }));
                                      }}
                                      title={isImportant ? 'Notifikasi Penting (Aktif)' : 'Tandai sebagai Notifikasi Penting'}
                                      className="p-1 rounded-lg hover:bg-muted transition-all"
                                    >
                                      <Star className={`w-4 h-4 transition-all ${
                                        isImportant
                                          ? 'fill-[#C9A227] text-[#C9A227] scale-110'
                                          : 'text-neutral-300 hover:text-neutral-400'
                                      }`} />
                                    </button>
                                  </div>

                                  {/* Push Switch Toggle */}
                                  <div className="sm:col-span-1 flex sm:justify-center items-center">
                                    <Switch
                                      disabled={activeMode === 'off'}
                                      checked={isPush}
                                      onCheckedChange={(checked) => handleNotifChannelToggle(event.id, 'push', checked)}
                                    />
                                  </div>

                                  {/* Scope Dropdown */}
                                  <div className="sm:col-span-2 flex justify-end">
                                    <div className="w-full sm:w-36">
                                      <Select
                                        value={activeMode}
                                        onValueChange={(val) => handleNotifModeChange(event.id, val)}
                                      >
                                        <SelectTrigger className="h-7 text-[11px] bg-muted/30">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="all" className="text-xs">
                                            Global (Semua)
                                          </SelectItem>
                                          {event.allowPicMode && (
                                            <SelectItem value="pic_only" className="text-xs text-[#8C6208] font-medium">
                                              Hanya PIC
                                            </SelectItem>
                                          )}
                                          <SelectItem value="off" className="text-xs text-rose-600">
                                            Nonaktif
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
